import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FlaskConical, Plus, Search, Truck, CheckCircle, XCircle,
  Package, Loader2, X, Filter, ArrowRight,
  Send, Eye, BarChart3
} from 'lucide-react';
import { api } from '@/utils/api';
import { formatDate } from '@/utils/formatters';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type SampleStatus = 'dispatched' | 'delivered' | 'approved' | 'rejected' | 'converted';

interface Sample {
  id: string;
  sampleNumber: string;
  buyerName: string;
  buyerAccountId: string | null;
  dealId: string | null;
  dealNumber: string | null;
  commodity: string;
  quantity: string;
  courierName: string;
  trackingNumber: string;
  dispatchDate: string;
  status: SampleStatus;
  notes: string;
  rejectionReason: string | null;
  convertedDealId: string | null;
  createdAt: string;
  updatedAt: string;
}

interface Account {
  id: string;
  name: string;
}

interface Deal {
  id: string;
  number: string;
  title?: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STATUS_BADGE: Record<SampleStatus, string> = {
  dispatched: 'bg-yellow-100 text-yellow-700',
  delivered: 'bg-blue-100 text-blue-700',
  approved: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
  converted: 'bg-purple-100 text-purple-700',
};

const COMMODITIES = [
  'NPK 19-19-19',
  'NPK 20-20-0',
  'NPK 12-32-16',
  'NPK 10-26-26',
  'NPK 15-15-15',
  'DAP',
  'MOP',
  'Urea',
  'SSP',
  'Potash',
  'Ammonium Sulphate',
  'Zinc Sulphate',
  'Other',
];

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'All Statuses' },
  { value: 'dispatched', label: 'Dispatched' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'converted', label: 'Converted' },
];

// ---------------------------------------------------------------------------
// Empty form
// ---------------------------------------------------------------------------

const EMPTY_FORM = {
  buyerAccountId: '',
  buyerNameManual: '',
  dealId: '',
  commodity: COMMODITIES[0],
  quantity: '',
  courierName: '',
  trackingNumber: '',
  dispatchDate: new Date().toISOString().slice(0, 10),
  notes: '',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SamplesPage() {
  const navigate = useNavigate();

  // Data
  const [samples, setSamples] = useState<Sample[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [loading, setLoading] = useState(true);

  // Create modal
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [useManualBuyer, setUseManualBuyer] = useState(false);

  // Rejection modal
  const [rejectModal, setRejectModal] = useState<string | null>(null); // sample id
  const [rejectionReason, setRejectionReason] = useState('');
  const [rejecting, setRejecting] = useState(false);

  // Status update loading per-sample
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // -----------------------------------------------------------------------
  // Fetch
  // -----------------------------------------------------------------------

  useEffect(() => {
    fetchData();
  }, [statusFilter]);

  const fetchData = async () => {
    try {
      const params = statusFilter ? `?status=${statusFilter}` : '';
      const [samplesRes, accountsRes, dealsRes] = await Promise.all([
        api.get(`/veda-agro/samples${params}`),
        api.get('/accounts?type=customer&limit=200'),
        api.get('/deals?limit=200'),
      ]);
      setSamples(samplesRes.data.data?.samples ?? samplesRes.data.data ?? []);
      setAccounts(accountsRes.data.data?.accounts ?? accountsRes.data.data ?? []);
      setDeals(dealsRes.data.data?.deals ?? dealsRes.data.data ?? []);
    } catch (err) {
      console.error('Failed to fetch samples data:', err);
    } finally {
      setLoading(false);
    }
  };

  // -----------------------------------------------------------------------
  // Create sample
  // -----------------------------------------------------------------------

  const handleCreate = async () => {
    const buyerName = useManualBuyer
      ? form.buyerNameManual.trim()
      : accounts.find(a => a.id === form.buyerAccountId)?.name || '';

    if (!buyerName) {
      alert('Please select or enter a buyer name');
      return;
    }
    if (!form.commodity || !form.quantity) {
      alert('Commodity and quantity are required');
      return;
    }

    setSaving(true);
    try {
      await api.post('/veda-agro/samples', {
        buyerAccountId: useManualBuyer ? null : form.buyerAccountId || null,
        buyerName,
        dealId: form.dealId || null,
        commodity: form.commodity,
        quantity: form.quantity,
        courierName: form.courierName,
        trackingNumber: form.trackingNumber,
        dispatchDate: form.dispatchDate,
        notes: form.notes,
      });
      setShowModal(false);
      setForm({ ...EMPTY_FORM });
      setUseManualBuyer(false);
      fetchData();
    } catch (err) {
      alert('Failed to create sample');
    } finally {
      setSaving(false);
    }
  };

  // -----------------------------------------------------------------------
  // Status transitions
  // -----------------------------------------------------------------------

  const updateStatus = async (sampleId: string, status: string, extra?: Record<string, string>) => {
    setUpdatingId(sampleId);
    try {
      await api.patch(`/veda-agro/samples/${sampleId}/status`, { status, ...extra });
      fetchData();
    } catch (err) {
      alert('Failed to update status');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleReject = async () => {
    if (!rejectModal) return;
    if (!rejectionReason.trim()) {
      alert('Please provide a rejection reason');
      return;
    }
    setRejecting(true);
    try {
      await api.patch(`/veda-agro/samples/${rejectModal}/status`, {
        status: 'rejected',
        rejectionReason: rejectionReason.trim(),
      });
      setRejectModal(null);
      setRejectionReason('');
      fetchData();
    } catch (err) {
      alert('Failed to reject sample');
    } finally {
      setRejecting(false);
    }
  };

  // -----------------------------------------------------------------------
  // Filtering
  // -----------------------------------------------------------------------

  const filtered = samples.filter(s => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      s.buyerName.toLowerCase().includes(q) ||
      s.commodity.toLowerCase().includes(q) ||
      s.sampleNumber.toLowerCase().includes(q) ||
      (s.courierName && s.courierName.toLowerCase().includes(q)) ||
      (s.trackingNumber && s.trackingNumber.toLowerCase().includes(q))
    );
  });

  // -----------------------------------------------------------------------
  // Stats
  // -----------------------------------------------------------------------

  const total = samples.length;
  const dispatched = samples.filter(s => s.status === 'dispatched').length;
  const delivered = samples.filter(s => s.status === 'delivered').length;
  const approved = samples.filter(s => s.status === 'approved').length;
  const rejected = samples.filter(s => s.status === 'rejected').length;
  const converted = samples.filter(s => s.status === 'converted').length;
  const decidedCount = approved + rejected + converted;
  const conversionRate = decidedCount > 0 ? Math.round(((approved + converted) / decidedCount) * 100) : 0;

  // -----------------------------------------------------------------------
  // Loading state
  // -----------------------------------------------------------------------

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
      </div>
    );
  }

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sample Dispatch Tracking</h1>
          <p className="text-sm text-gray-500">Track commodity samples sent to international buyers for approval</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)]"
        >
          <Plus size={16} /> New Sample
        </button>
      </div>

      {/* Stats Bar */}
      <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-7">
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <div className="flex items-center gap-2 text-sm text-gray-500"><Package size={16} /> Total</div>
          <p className="text-2xl font-bold text-gray-900">{total}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <div className="flex items-center gap-2 text-sm text-yellow-600"><Send size={16} /> Dispatched</div>
          <p className="text-2xl font-bold text-yellow-600">{dispatched}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <div className="flex items-center gap-2 text-sm text-blue-600"><Truck size={16} /> Delivered</div>
          <p className="text-2xl font-bold text-blue-600">{delivered}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <div className="flex items-center gap-2 text-sm text-green-600"><CheckCircle size={16} /> Approved</div>
          <p className="text-2xl font-bold text-green-600">{approved}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <div className="flex items-center gap-2 text-sm text-red-600"><XCircle size={16} /> Rejected</div>
          <p className="text-2xl font-bold text-red-600">{rejected}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <div className="flex items-center gap-2 text-sm text-purple-600"><ArrowRight size={16} /> Converted</div>
          <p className="text-2xl font-bold text-purple-600">{converted}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <div className="flex items-center gap-2 text-sm text-gray-500"><BarChart3 size={16} /> Conv. Rate</div>
          <p className="text-2xl font-bold text-gray-900">{conversionRate}%</p>
        </div>
      </div>

      {/* Search + Filter */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search by buyer, commodity, sample #, courier..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-4 text-sm"
          />
        </div>
        <div className="relative">
          <Filter size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <select
            value={statusFilter}
            onChange={e => { setStatusFilter(e.target.value); setLoading(true); }}
            className="rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-8 text-sm appearance-none"
          >
            {STATUS_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50 text-left">
                <th className="px-4 py-3 font-medium text-gray-500">Sample #</th>
                <th className="px-4 py-3 font-medium text-gray-500">Buyer</th>
                <th className="px-4 py-3 font-medium text-gray-500">Commodity</th>
                <th className="px-4 py-3 font-medium text-gray-500">Qty</th>
                <th className="px-4 py-3 font-medium text-gray-500">Dispatch Date</th>
                <th className="px-4 py-3 font-medium text-gray-500">Courier / Tracking</th>
                <th className="px-4 py-3 font-medium text-gray-500">Status</th>
                <th className="px-4 py-3 font-medium text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-gray-400">
                    <FlaskConical size={32} className="mx-auto mb-2 text-gray-300" />
                    {samples.length === 0 ? 'No samples dispatched yet' : 'No samples match your filters'}
                  </td>
                </tr>
              ) : (
                filtered.map(s => (
                  <tr key={s.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">{s.sampleNumber}</td>
                    <td className="px-4 py-3 text-gray-900">
                      <div>{s.buyerName}</div>
                      {s.dealNumber && (
                        <div className="text-xs text-gray-400">Deal: {s.dealNumber}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-700">{s.commodity}</td>
                    <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{s.quantity}</td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{formatDate(s.dispatchDate)}</td>
                    <td className="px-4 py-3 text-gray-600">
                      <div>{s.courierName || '-'}</div>
                      {s.trackingNumber && (
                        <div className="text-xs text-gray-400 font-mono">{s.trackingNumber}</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_BADGE[s.status]}`}>
                        {s.status.charAt(0).toUpperCase() + s.status.slice(1)}
                      </span>
                      {s.status === 'rejected' && s.rejectionReason && (
                        <div className="mt-1 text-xs text-red-500 max-w-[160px] truncate" title={s.rejectionReason}>
                          {s.rejectionReason}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-2">
                        {updatingId === s.id ? (
                          <Loader2 size={16} className="animate-spin text-gray-400" />
                        ) : (
                          <>
                            {s.status === 'dispatched' && (
                              <button
                                onClick={() => updateStatus(s.id, 'delivered')}
                                className="inline-flex items-center gap-1 rounded-md bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100"
                              >
                                <Truck size={12} /> Mark Delivered
                              </button>
                            )}
                            {s.status === 'delivered' && (
                              <>
                                <button
                                  onClick={() => updateStatus(s.id, 'approved')}
                                  className="inline-flex items-center gap-1 rounded-md bg-green-50 px-2.5 py-1 text-xs font-medium text-green-700 hover:bg-green-100"
                                >
                                  <CheckCircle size={12} /> Approve
                                </button>
                                <button
                                  onClick={() => { setRejectModal(s.id); setRejectionReason(''); }}
                                  className="inline-flex items-center gap-1 rounded-md bg-red-50 px-2.5 py-1 text-xs font-medium text-red-700 hover:bg-red-100"
                                >
                                  <XCircle size={12} /> Reject
                                </button>
                              </>
                            )}
                            {s.status === 'converted' && s.convertedDealId && (
                              <button
                                onClick={() => navigate(`/deals/${s.convertedDealId}`)}
                                className="inline-flex items-center gap-1 rounded-md bg-gray-50 px-2.5 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100"
                              >
                                <Eye size={12} /> View Deal
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Notes section below table for samples with notes */}
      {filtered.some(s => s.notes) && (
        <div className="mt-4 rounded-xl border border-gray-200 bg-white p-4">
          <h3 className="mb-3 text-sm font-medium text-gray-700">Sample Notes</h3>
          <div className="space-y-2">
            {filtered.filter(s => s.notes).map(s => (
              <div key={s.id} className="flex gap-3 text-sm">
                <span className="font-mono text-gray-400 whitespace-nowrap">{s.sampleNumber}</span>
                <span className="text-gray-600">{s.notes}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ---- Create Sample Modal ---- */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowModal(false)}>
          <div className="mx-4 w-full max-w-lg rounded-xl bg-white shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b px-6 py-4">
              <h2 className="text-lg font-semibold">Dispatch New Sample</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <div className="max-h-[70vh] overflow-y-auto p-6 space-y-4">
              {/* Buyer */}
              <div>
                <div className="mb-1 flex items-center justify-between">
                  <label className="block text-sm font-medium">Buyer <span className="text-red-500">*</span></label>
                  <button
                    type="button"
                    onClick={() => setUseManualBuyer(!useManualBuyer)}
                    className="text-xs text-[var(--ink-0)] hover:underline"
                  >
                    {useManualBuyer ? 'Select from accounts' : 'Enter manually'}
                  </button>
                </div>
                {useManualBuyer ? (
                  <input
                    type="text"
                    placeholder="Enter buyer name"
                    value={form.buyerNameManual}
                    onChange={e => setForm({ ...form, buyerNameManual: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  />
                ) : (
                  <select
                    value={form.buyerAccountId}
                    onChange={e => setForm({ ...form, buyerAccountId: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  >
                    <option value="">Select buyer account</option>
                    {accounts.map(a => (
                      <option key={a.id} value={a.id}>{a.name}</option>
                    ))}
                  </select>
                )}
              </div>

              {/* Link to Deal */}
              <div>
                <label className="mb-1 block text-sm font-medium">Link to Deal (optional)</label>
                <select
                  value={form.dealId}
                  onChange={e => setForm({ ...form, dealId: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                >
                  <option value="">No linked deal</option>
                  {deals.map(d => (
                    <option key={d.id} value={d.id}>{d.number}{d.title ? ` - ${d.title}` : ''}</option>
                  ))}
                </select>
              </div>

              {/* Commodity */}
              <div>
                <label className="mb-1 block text-sm font-medium">Commodity <span className="text-red-500">*</span></label>
                <select
                  value={form.commodity}
                  onChange={e => setForm({ ...form, commodity: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                >
                  {COMMODITIES.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>

              {/* Quantity */}
              <div>
                <label className="mb-1 block text-sm font-medium">Sample Quantity <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  placeholder="e.g., 500g, 1kg, 2 bags"
                  value={form.quantity}
                  onChange={e => setForm({ ...form, quantity: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              </div>

              {/* Courier + Tracking — side by side */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block text-sm font-medium">Courier Name</label>
                  <input
                    type="text"
                    placeholder="e.g., DHL, FedEx"
                    value={form.courierName}
                    onChange={e => setForm({ ...form, courierName: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium">Tracking Number</label>
                  <input
                    type="text"
                    placeholder="Courier tracking #"
                    value={form.trackingNumber}
                    onChange={e => setForm({ ...form, trackingNumber: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
              </div>

              {/* Dispatch Date */}
              <div>
                <label className="mb-1 block text-sm font-medium">Dispatch Date <span className="text-red-500">*</span></label>
                <input
                  type="date"
                  value={form.dispatchDate}
                  onChange={e => setForm({ ...form, dispatchDate: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              </div>

              {/* Notes */}
              <div>
                <label className="mb-1 block text-sm font-medium">Notes</label>
                <textarea
                  value={form.notes}
                  onChange={e => setForm({ ...form, notes: e.target.value })}
                  rows={3}
                  placeholder="Additional details about this sample dispatch..."
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 border-t px-6 py-4">
              <button onClick={() => setShowModal(false)} className="rounded-lg border border-gray-300 px-4 py-2 text-sm">Cancel</button>
              <button
                onClick={handleCreate}
                disabled={saving}
                className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white disabled:opacity-50 hover:bg-[var(--ink-1)]"
              >
                {saving ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                Dispatch Sample
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---- Rejection Reason Modal ---- */}
      {rejectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setRejectModal(null)}>
          <div className="mx-4 w-full max-w-sm rounded-xl bg-white shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b px-6 py-4">
              <h2 className="text-lg font-semibold text-red-700">Reject Sample</h2>
              <button onClick={() => setRejectModal(null)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-600">Please record why the buyer rejected this sample.</p>
              <div>
                <label className="mb-1 block text-sm font-medium">Rejection Reason <span className="text-red-500">*</span></label>
                <textarea
                  value={rejectionReason}
                  onChange={e => setRejectionReason(e.target.value)}
                  rows={3}
                  placeholder="e.g., Moisture content too high, wrong granule size, contamination..."
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  autoFocus
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 border-t px-6 py-4">
              <button onClick={() => setRejectModal(null)} className="rounded-lg border border-gray-300 px-4 py-2 text-sm">Cancel</button>
              <button
                onClick={handleReject}
                disabled={rejecting}
                className="flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50 hover:bg-red-700"
              >
                {rejecting ? <Loader2 size={16} className="animate-spin" /> : <XCircle size={16} />}
                Confirm Rejection
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
